import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  Inject,
  ViewChildren,
  QueryList
} from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

import {
  CommonRegEx,
  DropDownConfiguration,
  DropDownData,
  EntityCacheProvider,
  Site,
  NotificationService,
  UnmanagedDevicesService,
  UserService,
  TransformationService
} from '@nrwl/common-lib';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
  selector: 'add-unmanaged-device',
  templateUrl: './add-unmanaged-device.component.html',
  styleUrls: ['./add-unmanaged-device.component.scss']
})
export class AddUnmanagedDeviceComponent implements OnInit, OnDestroy {
  @ViewChild('deviceForm', { static: true }) form: NgForm;
  @ViewChildren(NgModel) inputFields!: QueryList<NgModel>;

  name: string;
  vendor: string;
  model: string;
  version: string;
  location: string;
  latitude: string;
  longitude: string;
  twampFullport: string;
  twampLightport: string;
  twampFullIpAddress: string;
  twampLightIpAddress: string;
  pingIpAddress: string;
  disableTwampFullCheckbox = false;
  disableTwampLightCheckbox = false;
  disablePingCheckbox = false;
  twampLRAvailable = true;
  twampRAvailable = false;
  type = 'twampLR';
  validIPRegex: RegExp = CommonRegEx.IP_V4_V6;
  validIPV4Regex: RegExp = CommonRegEx.IP_REGEX;
  validIPV6Regex: RegExp = CommonRegEx.IPV6;
  validMacRegex: RegExp = CommonRegEx.MAC;
  latitudeIPRegex: RegExp = CommonRegEx.LATITUDE;
  longitudeIPRegex: RegExp = CommonRegEx.LONGITUDE;
  wholeNumberReg = /^\d+$/;
  // eslint-disable-next-line
  deviceData: any;
  siteDropDown = [];
  selectedSite: DropDownData;
  editMode = false;
  dropdownConfiguration: DropDownConfiguration;
  // eslint-disable-next-line
  allDevices: any;
  isTwampFullChecked = false;
  isTwampLightChecked = false;
  isPingChecked = false;
  nameExist: boolean;
  isSiteCacheAvailable: boolean;
  showLoopbackDevicesTab = false;
  selectedTabIndex = 0;
  isL2LoopbackChecked = false;
  isL3Ipv4LoopbackChecked = false;
  isL3Ipv6LoopbackChecked = false;
  l2LoopbackMacAddress: string;
  l3Ipv4LoopbackIpAddress: string;
  l3Ipv6LoopbackIpAddress: string;
  disableL2LoopbackCheckBox = false;
  disableL3Ipv4LoopbackCheckBox = false;
  disableL3Ipv6LoopbackCheckBox = false;
  isLoading = false;
  reflectorsIpList: any[];
  loopbacksMacList: any[];
  loopbacksIpList: any[];
  activeTab = 'reflectors';
  ipAddressCheck = false;
  macAddressCheck = false;
  loopbackIpAddressCheck = false;
  twampFullIpError = false;
  twampLightIpError = false;
  pingIpError = false;
  l3Ipv4LoopbackIpError = false;
  l3Ipv6LoopbackIpError = false;
  existingIPs: Set<unknown>;
  private readonly unsubscribe: Subject<void> = new Subject();
  private cacheObj: Subscription;

  constructor(
    public dialogRef: MatDialogRef<AddUnmanagedDeviceComponent>,
    readonly entityCache: EntityCacheProvider,
    private unmanagedDevicesService: UnmanagedDevicesService,
    private notificationService: NotificationService,
    private readonly userService: UserService,
    private transformService: TransformationService,
    @Inject(MAT_DIALOG_DATA) public data: any // eslint-disable-line
  ) {
    this.nameExist = false;
  }

  ngOnInit() {
    if (this.data && this.data.editMode) {
      this.editMode = true;
      this.assignData(this.data.device.deviceData);
      this.storeExistingIPs();
    }
    this.loadAllUnmanagedDevices();
    this.getDropDownConfiguration();
    this.fetchSiteData();
    if (this.userService.isFusionUIProject()) {
      this.showLoopbackDevicesTab = this.data?.showLoopbackDevicesTab ?? true;
      this.activeTab = this.selectedTabIndex === 1 ? 'loopbacks' : 'reflectors';
    } else {
      this.showLoopbackDevicesTab = false;
    }
  }

  storeExistingIPs(): void {
    this.existingIPs = new Set();

    if (this.twampFullIpAddress) {
      this.existingIPs.add(this.twampFullIpAddress);
    }
    if (this.twampLightIpAddress) {
      this.existingIPs.add(this.twampLightIpAddress);
    }
    if (this.pingIpAddress) {
      this.existingIPs.add(this.pingIpAddress);
    }
    if (this.l3Ipv4LoopbackIpAddress) {
      this.existingIPs.add(this.l3Ipv4LoopbackIpAddress);
    }
    if (this.l3Ipv6LoopbackIpAddress) {
      this.existingIPs.add(this.l3Ipv6LoopbackIpAddress);
    }
  }

  getDropDownConfiguration(): void {
    this.dropdownConfiguration = {
      isDisableRipple: true,
      outline: 'outline',
      isDisableOptionCentering: true,
      className: 'site-dropdown',
      panelClass: 'site-panel',
      placeholderValue: 'Choose a site where device is located',
      isSearchable: true,
      selectTriggerImgSrc: 'images/map-marker.svg',
      isDividerEnableInOptions: true,
      isSelectAll: true,
      isWarningAvailable: true,
      isDisabled: this.editMode ? true : false,
      searchConfiguration: {
        isNameAllowed: true,
        isSubContentAllowed: true,
        isSubContent1Allowed: true,
        isSubDetailsAllowed: true
      },
      enableRowIcons: false
    };
  }

  fetchSiteData(): void {
    this.isSiteCacheAvailable = false;
    const siteEntity = this.entityCache.findEntityCache('site');
    if (siteEntity) {
      this.isSiteCacheAvailable = true;
      const siteList = this.entityCache.findEntityCache('site').getValues();
      this.siteDropDown = this.getSiteDropDownFormatData(siteList);
      if (this.data?.editMode) {
        this.siteSelection({ key: this.data.device.deviceData.site.toString() });
      }
    }
  }

  getSiteDropDownFormatData(listData: Site[]): DropDownData[] {
    return this.getDropDownData(listData);
  }

  getDropDownData(siteList: Array<Site>): DropDownData[] {
    const dropDownData = [];
    let tempData: DropDownData = {
      value: '',
      key: '',
      subContent: '',
      locationDetail: { lat: 0, lon: 0 }
    };
    siteList?.map(element => {
      tempData = Object.assign({}, tempData);
      tempData.value = element.sitesData.name;
      tempData.subContent = element.sitesData.locationname;
      tempData.key = element.sitesData.id.toString();
      tempData.locationDetail.lat = element.sitesData.location.lat;
      tempData.locationDetail.lon = element.sitesData.location.lon;
      dropDownData.push(tempData);
    });
    return dropDownData;
  }

  //eslint-disable-next-line
  siteSelection(event: any): void {
    this.selectedSite = this.siteDropDown.find(x => x.key === event.key);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
  }

  onTypeChange(data: string, editMode = false): void {
    this.type = data;
    if (this.type === 'twampLR') {
      this.twampLRAvailable = true;
      this.twampRAvailable = false;
    } else if (this.type === 'twampR') {
      this.twampRAvailable = true;
      this.twampLRAvailable = false;
    } else {
      this.twampRAvailable = false;
      this.twampLRAvailable = false;
    }
    if (!editMode) {
      this.twampFullport = '';
      this.twampLightIpAddress = '';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onChecked(reflectorType: string, event: any): void {
    if (reflectorType === 'twampFull') {
      this.isTwampFullChecked = event.checked;
      if (!event.checked) {
        this.twampFullIpAddress = ''; // Clear the input
        this.twampFullIpError = false; // Reset the error
      }
    } else if (reflectorType === 'twampLight') {
      this.isTwampLightChecked = event.checked;
      if (!event.checked) {
        this.twampLightIpAddress = '';
        this.twampLightIpError = false;
      }
    } else if (reflectorType === 'ping') {
      this.isPingChecked = event.checked;
      if (!event.checked) {
        this.pingIpAddress = '';
        this.pingIpError = false;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onCheckLoopbackOption(loopbackType: string, event: any): void {
    if (loopbackType === 'L2Loopback') {
      this.isL2LoopbackChecked = event.checked;
      if (!event.checked) {
        this.l2LoopbackMacAddress = ''; // Clear the input
        this.macAddressCheck = false; // Remove the error
      }
    } else if (loopbackType === 'L3Ipv4Loopback') {
      this.isL3Ipv4LoopbackChecked = event.checked;
      if (!event.checked) {
        this.l3Ipv4LoopbackIpAddress = '';
        this.l3Ipv4LoopbackIpError = false;
      }
    } else if (loopbackType === 'L3Ipv6Loopback') {
      this.isL3Ipv6LoopbackChecked = event.checked;
      if (!event.checked) {
        this.l3Ipv6LoopbackIpAddress = '';
        this.l3Ipv6LoopbackIpError = false;
      }
    }
  }

  saveDevice(): void {
    const payload = this.createPayLoad();
    if (this.data?.editMode) {
      this.updateDevice(payload);
    } else {
      this.createDevices(payload);
    }
  }

  nameUpdated(): void {
    this.nameExist = false;
    if (this.name) {
      this.checkNameAlreadyExists();
      const pattern = new RegExp('[?%/]');
      if (pattern.test(this.name)) {
        this.form.controls.name?.setErrors({ regex: 'wrong' });
        this.form.controls.name?.markAsDirty();
      }
    }
  }

  // eslint-disable-next-line
  createPayLoad(): Array<any> {
    const payload = [];
    const formData = this.form.form.value;
    const finalDataObj = {
      id: this.data && this.data.editMode ? this.data?.device?.deviceData?.id : null,
      tenantId: null,
      type: 'unmanaged',
      alias: formData.name,
      name: this.data && this.data.editMode ? this.data?.device?.deviceData?.id : null
    };
    const modelDataObj = {
      model: formData.model ? formData.model : null,
      vendor: formData.vendor,
      version: formData.version
    };
    const extentionObj = {};
    const locationObj = {
      address: this.selectedSite
        ? this.selectedSite.key
        : formData.location
        ? formData.location
        : ''
    };
    const positionDataObj = {
      lat: this.selectedSite
        ? this.selectedSite.locationDetail.lat
        : formData.latitude
        ? formData.latitude
        : 0,
      lon: this.selectedSite
        ? this.selectedSite.locationDetail.lon
        : formData.longitude
        ? formData.longitude
        : 0
    };
    if (positionDataObj && Object.keys(positionDataObj).length > 0) {
      locationObj['position'] = positionDataObj;
    }
    if (locationObj && Object.keys(locationObj).length > 0) {
      extentionObj['location'] = locationObj;
    }
    const capabilitiesObj = {};
    if (this.isTwampFullChecked) {
      const twampReflectorObj = { ipv6: false };
      twampReflectorObj['max-control-sessions'] = '';
      twampReflectorObj['max-frame-size'] = '';
      twampReflectorObj['max-flows'] = '';
      twampReflectorObj['control-server-ip'] = formData.twampFullIpAddress.trim();
      twampReflectorObj['control-server-port'] = formData.twampFullport;
      if (twampReflectorObj && Object.keys(twampReflectorObj).length > 0) {
        capabilitiesObj['twamp-reflector'] = twampReflectorObj;
      }
    }
    if (this.isTwampLightChecked) {
      const twampReflectorLightObj = { ipv6: false };
      twampReflectorLightObj['max-flows-per-test'] = '';
      twampReflectorLightObj['max-frame-size'] = '';
      twampReflectorLightObj['ip'] = formData.twampLightIpAddress.trim();
      twampReflectorLightObj['udp-port'] = formData.twampLightport;
      if (twampReflectorLightObj && Object.keys(twampReflectorLightObj).length > 0) {
        capabilitiesObj['twamp-light-reflector'] = twampReflectorLightObj;
      }
    }
    if (this.isPingChecked) {
      const pingObj = { ip: formData.pingIpAddress };
      if (pingObj && Object.keys(pingObj).length > 0) {
        capabilitiesObj['twamp-ping-reflector'] = pingObj;
      }
    }
    if (this.isL2LoopbackChecked) {
      const Obj = { mac: formData.l2LoopbackMacAddress };
      if (Obj && Object.keys(Obj).length > 0) {
        capabilitiesObj['l2-loopback'] = Obj;
      }
    }
    if (this.isL3Ipv4LoopbackChecked) {
      const Obj = { ip: formData.l3Ipv4LoopbackIpAddress };
      if (Obj && Object.keys(Obj).length > 0) {
        capabilitiesObj['l3-ipv4-loopback'] = Obj;
      }
    }
    if (this.isL3Ipv6LoopbackChecked) {
      const Obj = { ip: formData.l3Ipv6LoopbackIpAddress };
      if (Obj && Object.keys(Obj).length > 0) {
        capabilitiesObj['l3-ipv6-loopback'] = Obj;
      }
    }
    if (capabilitiesObj && Object.keys(capabilitiesObj).length > 0) {
      extentionObj['capabilities'] = capabilitiesObj;
    }
    if (modelDataObj && Object.keys(modelDataObj).length > 0) {
      finalDataObj['model'] = modelDataObj;
    }
    if (extentionObj && Object.keys(extentionObj).length > 0) {
      finalDataObj['extension'] = extentionObj;
    }
    if (finalDataObj && Object.keys(finalDataObj).length > 0) {
      payload.push(finalDataObj);
    }
    return payload;
  }
  loadAllUnmanagedDevices(): void {
    this.cacheObj = this.unmanagedDevicesService.cache.subscribe(res => {
      this.allDevices = res;
      this.extractDeviceData();
    });
  }

  extractDeviceData(): any {
    // Reset collected data
    this.reflectorsIpList = [];
    this.loopbacksMacList = [];
    this.loopbacksIpList = [];

    this.allDevices.forEach(device => {
      const deviceData = device.deviceData;
      if (!deviceData) return;

      // Collect Reflectors/ Loopbacks MACs & IPs
      if (deviceData.capabilities) {
        Object.keys(deviceData.capabilities).forEach(key => {
          const data = deviceData.capabilities[key];
          if (data.mac) {
            this.loopbacksMacList.push(data.mac);
          }
          if (
            key === 'twamp-light-reflector' ||
            key === 'twamp-reflector' ||
            key === 'twamp-ping-reflector'
          ) {
            // Collect Reflectors IPs
            if (data.ip) {
              this.reflectorsIpList.push(data.ip);
            } else if (data['control-server-ip']) {
              this.reflectorsIpList.push(data['control-server-ip']);
            }
          } else {
            if (data.ip) {
              // Collect Loopbacks IPs
              this.loopbacksIpList.push(data.ip);
            }
          }
        });
      }
    });
  }

  validateInput(fieldName: string, value: string): void {
    const trimmedValue = value.trim();

    // In edit mode, exclude the existing IPs from being checked
    if (this.editMode && this.existingIPs.has(trimmedValue)) {
      return; // Don't validate existing IPs
    }

    if (this.activeTab === 'reflectors') {
      if (fieldName === 'twampFullIpAddress' && this.isTwampFullChecked) {
        this.twampFullIpError = this.reflectorsIpList.includes(trimmedValue);
      } else if (fieldName === 'twampLightIpAddress' && this.isTwampLightChecked) {
        this.twampLightIpError = this.reflectorsIpList.includes(trimmedValue);
      } else if (fieldName === 'pingIpAddress' && this.isPingChecked) {
        this.pingIpError = this.reflectorsIpList.includes(trimmedValue);
      } else {
        // Reset error if checkbox is unchecked
        this.twampFullIpError = false;
        this.twampLightIpError = false;
        this.pingIpError = false;
      }
    } else if (this.activeTab === 'loopbacks') {
      if (fieldName.includes('mac') && this.isL2LoopbackChecked) {
        this.macAddressCheck = this.loopbacksMacList.includes(trimmedValue);
      } else if (fieldName === 'l3Ipv4LoopbackIpAddress' && this.isL3Ipv4LoopbackChecked) {
        this.l3Ipv4LoopbackIpError = this.loopbacksIpList.includes(trimmedValue);
      } else if (fieldName === 'l3Ipv6LoopbackIpAddress' && this.isL3Ipv6LoopbackChecked) {
        this.l3Ipv6LoopbackIpError = this.loopbacksIpList.includes(trimmedValue);
      } else {
        // Reset error if checkbox is unchecked
        this.macAddressCheck = false;
        this.l3Ipv4LoopbackIpError = false;
        this.l3Ipv6LoopbackIpError = false;
      }
    }
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.activeTab = event.index === 0 ? 'reflectors' : 'loopbacks';
  }

  //eslint-disable-next-line
  checkNameAlreadyExists(): void {
    if (!this.name?.trim()) {
      this.nameExist = false;
      return;
    }

    const nameToCheck = this.name.trim().toLowerCase();

    // Filter out the current device in edit mode
    const filteredDevices = this.allDevices.filter(
      item => !(this.data?.editMode && item.deviceData.id === this.data.device.deviceData.id)
    );

    // Check if any other device has the same name
    this.nameExist = filteredDevices.some(
      item => item.deviceData.name.trim().toLowerCase() === nameToCheck
    );
  }

  //eslint-disable-next-line
  assignData(data: any): void {
    this.isTwampFullChecked = false;
    this.isTwampLightChecked = false;
    this.isPingChecked = false;
    this.isL2LoopbackChecked = false;
    this.isL3Ipv4LoopbackChecked = false;
    this.isL3Ipv6LoopbackChecked = false;
    this.name = data.name;
    this.vendor = data?.model?.vendor;
    this.model = data?.model?.model;
    this.version = data?.model?.version;
    this.location = data.location;
    this.latitude = data.lat;
    this.longitude = data.lon;
    if (data?.capabilities) {
      if (data.capabilities['twamp-reflector']) {
        this.twampFullIpAddress = data?.capabilities['twamp-reflector']['control-server-ip'];
        this.twampFullport = data?.capabilities['twamp-reflector']['control-server-port'];
        this.isTwampFullChecked = true;
        this.disableTwampFullCheckbox = true;
      }
      if (data?.capabilities['twamp-light-reflector']) {
        this.twampLightIpAddress = data?.capabilities['twamp-light-reflector'].ip;
        this.twampLightport = data?.capabilities['twamp-light-reflector']['udp-port'];
        this.isTwampLightChecked = true;
        this.disableTwampLightCheckbox = true;
      }
      if (data?.capabilities['twamp-ping-reflector']) {
        this.pingIpAddress = data?.capabilities['twamp-ping-reflector'].ip;
        this.isPingChecked = true;
        this.disablePingCheckbox = true;
      }
      if (data?.capabilities['l2-loopback']) {
        this.isL2LoopbackChecked = true;
        this.l2LoopbackMacAddress = data?.capabilities['l2-loopback'].mac;
        this.disableL2LoopbackCheckBox = true;
      }
      if (data?.capabilities['l3-ipv4-loopback']) {
        this.isL3Ipv4LoopbackChecked = true;
        this.l3Ipv4LoopbackIpAddress = data?.capabilities['l3-ipv4-loopback'].ip;
        this.disableL3Ipv4LoopbackCheckBox = true;
      }
      if (data?.capabilities['l3-ipv6-loopback']) {
        this.isL3Ipv6LoopbackChecked = true;
        this.l3Ipv6LoopbackIpAddress = data?.capabilities['l3-ipv6-loopback'].ip;
        this.disableL3Ipv6LoopbackCheckBox = true;
      }
      if (!this.isTwampFullChecked && !this.isTwampLightChecked && !this.isPingChecked) {
        this.selectedTabIndex = 1;
      }
    }
  }

  //eslint-disable-next-line
  createDevices(payload: any): void {
    this.isLoading = true;
    this.unmanagedDevicesService
      .addUnmanagedDevices(payload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.notificationService.onSuccess(res.message);
          this.isLoading = false;
          this.unmanagedDevicesService.getUpdatedDevices().subscribe((response: any) => {
            const createdDevice = response.filter(
              device => device.deviceData.deviceName === payload[0].alias
            );
            const transformData = this.transformService.transformData(
              'twamp-destination-selection',
              createdDevice
            );
            this.dialogRef.close({ newCart: transformData.data, newTableRow: createdDevice });
          });
        },
        err => {
          this.isLoading = false;
          this.dialogRef.close();
          this.notificationService.onFail(err.error.message);
        }
      );
  }

  //eslint-disable-next-line
  updateDevice(payload: any): void {
    this.unmanagedDevicesService
      .updateUnmanagedDevice(payload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.notificationService.onSuccess(res.message);
          this.dialogRef.close();
        },
        err => {
          this.notificationService.onFail(err.error.message);
          this.dialogRef.close();
        }
      );
  }

  saveBtnDisabled(): boolean {
    return (
      this.nameExist || // If name exists, disable
      (this.isTwampFullChecked && this.twampFullIpError) ||
      (this.isTwampLightChecked && this.twampLightIpError) ||
      (this.isPingChecked && this.pingIpError) ||
      (this.isL3Ipv4LoopbackChecked && this.l3Ipv4LoopbackIpError) ||
      (this.isL3Ipv6LoopbackChecked && this.l3Ipv6LoopbackIpError) ||
      !this.isAnyCheckboxChecked() || // Ensure at least one checkbox is checked
      this.isAnyFieldInvalid() // Check form control validity
    );
  }

  isAnyCheckboxChecked(): boolean {
    return (
      this.isTwampFullChecked ||
      this.isTwampLightChecked ||
      this.isPingChecked ||
      this.isL2LoopbackChecked ||
      this.isL3Ipv4LoopbackChecked ||
      this.isL3Ipv6LoopbackChecked
    );
  }

  isAnyFieldInvalid(): boolean {
    return (
      this.inputFields &&
      this.inputFields.some(field => field.invalid && this.isFieldRelevant(field))
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  isFieldRelevant(field): boolean {
    switch (field.name) {
      case 'twampFullIpAddress':
      case 'twampFullport':
        return this.isTwampFullChecked;
      case 'twampLightIpAddress':
      case 'twampLightport':
        return this.isTwampLightChecked;
      case 'pingIpAddress':
        return this.isPingChecked;
      case 'l2LoopbackMacAddress':
        return this.isL2LoopbackChecked;
      case 'l3Ipv4LoopbackIpAddress':
        return this.isL3Ipv4LoopbackChecked;
      case 'l3Ipv6LoopbackIpAddress':
        return this.isL3Ipv6LoopbackChecked;
      default:
        return false;
    }
  }
}
